import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
      >
        <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute top-3 left-3 flex gap-2">
            {article.isTop && (
              <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-foreground-950/80 text-background-50 whitespace-nowrap">
                置顶
              </span>
            )}
            {article.isHot && (
              <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
                热门
              </span>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/20 text-white mb-3 whitespace-nowrap">
              {categoryMap[article.category] || article.category}
            </span>
            <h3 className="text-lg md:text-xl font-bold text-white leading-snug mb-2 line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-white/70 line-clamp-2 mb-3">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-white/50">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.comments > 10000 ? `${(article.comments / 10000).toFixed(1)}万` : article.comments}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg transition-all duration-200 hover:bg-background-100"
      >
        <div className="relative w-24 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-foreground-700 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-xs"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative w-full h-[200px] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {article.isTop && (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-foreground-950/80 text-background-50 whitespace-nowrap">
              置顶
            </span>
          )}
          {article.isHot && (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
        </div>
        <div className="absolute bottom-2 left-2">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-background-50/20 text-white whitespace-nowrap">
            {categoryMap[article.category] || article.category}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-950 leading-snug mb-2 line-clamp-2 group-hover:text-foreground-700 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.publishDate}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.comments}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}